import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { fetchExercises, searchExercises, BODY_PARTS, EQUIPMENT_TYPES } from '../../services/exerciseService';
import { addWorkoutLog, getWorkoutLogs } from '../../services/firestoreService';
import { consumePendingExercises } from '../../data/muscleExplorerData';
import MuscleMap from '../shared/MuscleMap';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

// ─── Rest Period Logic ─────────────────────────────────────
function getRestRec(exercise) {
  const bp = (exercise?.bodyPart || '').toLowerCase();
  const equip = (exercise?.equipment || '').toLowerCase();
  const target = (exercise?.target || '').toLowerCase();
  const name = exercise?.name ? exercise.name.charAt(0).toUpperCase() + exercise.name.slice(1) : 'This exercise';

  if (bp === 'cardio' || target === 'cardiovascular system') {
    return {
      seconds: 0,
      label: 'Optional',
      explanation: 'Cardio-based movement — your heart rate is your guide. Rest only until breathing is controlled enough to maintain safe form on the next interval.',
    };
  }

  if (equip === 'body weight' && (target === 'abs' || bp === 'waist')) {
    return {
      seconds: 45,
      label: '30–60 sec',
      explanation: 'Bodyweight core work fatigues the abs locally but doesn\'t tax the central nervous system heavily. Short rest keeps metabolic stress high without over-recovering.',
    };
  }

  if (equip === 'body weight') {
    return {
      seconds: 45,
      label: '30–60 sec',
      explanation: `${name} is bodyweight-based — heart rate spikes without heavy CNS demand. Rest until breathing normalizes, usually 30–60 seconds, then go again.`,
    };
  }

  // Single-joint isolation
  const isolationTargets = ['biceps', 'triceps', 'forearms', 'calves', 'tibialis anterior', 'brachialis'];
  const isolationBodyParts = ['lower arms', 'lower legs', 'upper arms'];
  if (isolationTargets.includes(target) || isolationBodyParts.includes(bp)) {
    return {
      seconds: 75,
      label: '60–90 sec',
      explanation: `${name} is a single-joint isolation targeting the ${target}. Fatigue stays local — blood clears lactate quickly and the CNS barely registers the load. More than 90 seconds here adds no benefit.`,
    };
  }

  // Shoulder compounds
  if (bp === 'shoulders' || target === 'delts') {
    return {
      seconds: 90,
      label: '60–90 sec',
      explanation: `${name} recruits the deltoids along with multiple rotator cuff stabilizers. Give those stabilizers 60–90 seconds to recover so your shoulder mechanics stay solid on the next set.`,
    };
  }

  // Heavy multi-joint compound
  const compoundBodyParts = ['back', 'chest', 'upper legs'];
  const compoundTargets = ['glutes', 'quads', 'hamstrings', 'pectorals', 'lats', 'upper back', 'spine'];
  if (compoundBodyParts.includes(bp) || compoundTargets.includes(target)) {
    return {
      seconds: 150,
      label: '2–3 min',
      explanation: `${name} is a compound, multi-joint movement with high CNS demand. Neural output drops significantly if you don't fully recover — cutting this rest short will kill your next set's performance. Two to three minutes is the science-backed minimum.`,
    };
  }

  return {
    seconds: 90,
    label: '60–90 sec',
    explanation: `Rest 60–90 seconds between sets. Adjust up if you're working near your max — heavier loads need more CNS recovery time.`,
  };
}

// ─── GIF Demonstration ────────────────────────────────────
function GifDisplay({ url, theme }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <style>{`@keyframes skel-pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }`}</style>
      <div style={{ fontSize: '10px', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>
        How To Do It
      </div>
      {!loaded && (
        <div style={{ width: '100%', height: '200px', borderRadius: '10px', background: theme.border, animation: 'skel-pulse 1.4s ease-in-out infinite' }} />
      )}
      <img
        src={url}
        alt="Exercise demonstration"
        onLoad={() => setLoaded(true)}
        style={{ width: '100%', borderRadius: '10px', display: loaded ? 'block' : 'none' }}
      />
    </div>
  );
}

export default function WorkoutTracker() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState('');
  const [bodyPart, setBodyPart] = useState('all');
  const [equipment, setEquipment] = useState('all');
  const [loading, setLoading] = useState(true);
  const [workoutSession, setWorkoutSession] = useState([]);
  const [workoutName, setWorkoutName] = useState('');
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('browse');
  const [selectedEx, setSelectedEx] = useState(null);

  // ── Rest Timer state ──
  const [restState, setRestState] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRestTimer = useCallback((exercise) => {
    const rec = getRestRec(exercise);
    if (rec.seconds === 0) {
      toast.success('Set logged! Rest as needed for cardio.');
      return;
    }
    clearTimer();
    setRestState({ ...rec, secondsLeft: rec.seconds, maxSeconds: rec.seconds });
    timerRef.current = setInterval(() => {
      setRestState(prev => {
        if (!prev || prev.secondsLeft <= 1) {
          clearTimer();
          toast.success('Rest complete — next set!', { duration: 3000 });
          return null;
        }
        return { ...prev, secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  useEffect(() => {
    loadExercises();
    if (user) getWorkoutLogs(user.uid).then(setLogs);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'session' || tabParam === 'browse' || tabParam === 'history') {
      setTab(tabParam);
    }
    const bp = searchParams.get('bodyPart');
    if (bp && BODY_PARTS.includes(bp)) setBodyPart(bp);

    const pending = consumePendingExercises();
    if (pending.length) {
      setWorkoutSession(prev => {
        const names = new Set(prev.map(e => e.name.toLowerCase()));
        const extras = pending
          .filter(e => e?.name && !names.has(e.name.toLowerCase()))
          .map(e => ({ ...e, sets: e.sets || [{ reps: '', weight: '', logged: false }] }));
        return extras.length ? [...prev, ...extras] : prev;
      });
      setTab('session');
      toast.success(`Added ${pending.length} exercise${pending.length === 1 ? '' : 's'} from Muscle Explorer`);
    }
  }, [searchParams]);

  const loadExercises = async () => {
    setLoading(true);
    const data = await fetchExercises(bodyPart);
    setExercises(data);
    setLoading(false);
  };

  useEffect(() => { loadExercises(); }, [bodyPart]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    if (!search) return loadExercises();
    setLoading(true);
    const data = await searchExercises(search);
    setExercises(data);
    setLoading(false);
  };

  const filteredExercises = exercises.filter(e =>
    (equipment === 'all' || e.equipment === equipment) &&
    (search === '' || e.name.toLowerCase().includes(search.toLowerCase()))
  );

  const addToSession = (ex) => {
    setWorkoutSession(prev => [...prev, { ...ex, sets: [{ reps: '', weight: '', logged: false }] }]);
    toast.success(`${ex.name} added to workout`);
  };

  const updateSet = (exIdx, setIdx, field, value) => {
    setWorkoutSession(prev => prev.map((e, i) => i === exIdx ? {
      ...e,
      sets: e.sets.map((s, j) => j === setIdx ? { ...s, [field]: value } : s),
    } : e));
  };

  const logSet = (exIdx, setIdx) => {
    setWorkoutSession(prev => prev.map((e, i) => i === exIdx ? {
      ...e,
      sets: e.sets.map((s, j) => j === setIdx ? { ...s, logged: true } : s),
    } : e));
    const exercise = workoutSession[exIdx];
    startRestTimer(exercise);
    toast.success('Set logged!', { duration: 1500 });
  };

  const addSet = (exIdx) => {
    setWorkoutSession(prev => prev.map((e, i) => i === exIdx
      ? { ...e, sets: [...e.sets, { reps: '', weight: '', logged: false }] } : e));
  };

  const removeExercise = (exIdx) => {
    setWorkoutSession(prev => prev.filter((_, i) => i !== exIdx));
  };

  const saveWorkout = async () => {
    if (!workoutName || workoutSession.length === 0) {
      toast.error('Add a workout name and at least one exercise');
      return;
    }
    try {
      clearTimer();
      setRestState(null);
      await addWorkoutLog(user.uid, { name: workoutName, exercises: workoutSession });
      toast.success('Workout saved! 💪');
      setWorkoutSession([]);
      setWorkoutName('');
      const updated = await getWorkoutLogs(user.uid);
      setLogs(updated);
    } catch {
      toast.error('Failed to save workout');
    }
  };

  const tabStyle = (t) => ({
    padding: '0.6rem 1.25rem', border: 'none', cursor: 'pointer',
    background: tab === t ? theme.accent : 'transparent',
    color: tab === t ? '#fff' : theme.textSecondary,
    borderRadius: '8px', fontFamily: "'Exo 2', sans-serif",
    fontWeight: tab === t ? 600 : 400, transition: 'all 0.2s ease',
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', paddingBottom: restState ? '120px' : '2rem' }} className="animate-fade">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text }}>
          💪 Workout Tracker
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/muscles" className="btn btn-ghost" style={{ padding: '0.45rem 0.9rem', fontSize: '0.85rem' }}>
            🧍 Muscle Map
          </Link>
          {workoutSession.length > 0 && (
            <span style={{ background: theme.accent, color: '#fff', borderRadius: '20px', padding: '0.25rem 0.75rem', fontSize: '0.8rem', fontWeight: 600 }}>
              {workoutSession.length} exercise{workoutSession.length !== 1 ? 's' : ''} in session
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {['browse', 'session', 'history'].map(t => (
          <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>
            {t === 'browse' ? '🔍 Browse' : t === 'session' ? `⚡ Session${workoutSession.length > 0 ? ` (${workoutSession.length})` : ''}` : '📋 History'}
          </button>
        ))}
      </div>

      {/* ── Browse Tab ── */}
      {tab === 'browse' && (
        <div>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: '200px', display: 'flex', gap: '0.5rem' }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search exercises..." style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={handleSearch}>Search</button>
            </div>
            <select value={bodyPart} onChange={e => setBodyPart(e.target.value)} style={{ flex: 1, minWidth: '150px' }}>
              {BODY_PARTS.map(b => <option key={b} value={b}>{b === 'all' ? 'All Body Parts' : b}</option>)}
            </select>
            <select value={equipment} onChange={e => setEquipment(e.target.value)} style={{ flex: 1, minWidth: '150px' }}>
              {EQUIPMENT_TYPES.map(e => <option key={e} value={e}>{e === 'all' ? 'All Equipment' : e}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>Loading exercises...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {filteredExercises.slice(0, 30).map(ex => (
                <div key={ex.id} className="card" style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
                  {ex.gifUrl ? (
                    <img src={ex.gifUrl} alt={ex.name} style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '8px', marginBottom: '0.75rem' }} />
                  ) : (
                    <div style={{ height: '80px', background: theme.border, borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>💪</div>
                  )}
                  <div style={{ fontWeight: 600, color: theme.text, marginBottom: '0.5rem', textTransform: 'capitalize' }}>{ex.name}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    {[ex.bodyPart, ex.equipment].map((tag, i) => (
                      <span key={i} style={{ background: theme.border, color: theme.textSecondary, borderRadius: '20px', padding: '0.2rem 0.6rem', fontSize: '0.7rem', textTransform: 'capitalize' }}>{tag}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', padding: '0.5rem' }} onClick={() => setSelectedEx(ex)}>View</button>
                    <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '0.5rem' }} onClick={() => addToSession(ex)}>+ Add</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Session Tab ── */}
      {tab === 'session' && (
        <div>
          <div className="form-group" style={{ maxWidth: '400px' }}>
            <label>Workout Name</label>
            <input value={workoutName} onChange={e => setWorkoutName(e.target.value)} placeholder="e.g. Push Day, Leg Day..." />
          </div>
          {workoutSession.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>💪</div>
              <p>Browse exercises and add them to your session</p>
              <button className="btn btn-ghost" style={{ marginTop: '1rem' }} onClick={() => setTab('browse')}>Browse Exercises</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {workoutSession.map((ex, exIdx) => {
                const rec = getRestRec(ex);
                return (
                  <div key={exIdx} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <div style={{ fontWeight: 600, color: theme.text, textTransform: 'capitalize', fontSize: '1rem' }}>{ex.name}</div>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: '1.2rem' }}
                        onClick={() => removeExercise(exIdx)}>✕</button>
                    </div>

                    {/* Rest recommendation */}
                    <div style={{
                      background: theme.bgSecondary,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      padding: '0.6rem 0.875rem',
                      marginBottom: '0.875rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                    }}>
                      <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>⏱</span>
                      <div>
                        <span style={{ color: theme.accent, fontWeight: 600, fontSize: '0.82rem', fontFamily: "'Rajdhani', sans-serif" }}>
                          Rest: {rec.label}
                        </span>
                        <span style={{ color: theme.textSecondary, fontSize: '0.78rem', marginLeft: '6px' }}>
                          {rec.explanation.length > 120 ? rec.explanation.slice(0, 120) + '…' : rec.explanation}
                        </span>
                      </div>
                    </div>

                    {/* Sets */}
                    {ex.sets.map((set, setIdx) => (
                      <div key={setIdx} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                        <span style={{
                          color: set.logged ? theme.success : theme.textSecondary,
                          fontSize: '0.8rem', minWidth: '48px', fontWeight: set.logged ? 600 : 400,
                        }}>
                          {set.logged ? '✓' : ''} Set {setIdx + 1}
                        </span>
                        <input type="number" placeholder="Reps" value={set.reps}
                          onChange={e => updateSet(exIdx, setIdx, 'reps', e.target.value)}
                          style={{ width: '72px', opacity: set.logged ? 0.5 : 1 }} />
                        <input type="number" placeholder="lbs" value={set.weight}
                          onChange={e => updateSet(exIdx, setIdx, 'weight', e.target.value)}
                          style={{ width: '72px', opacity: set.logged ? 0.5 : 1 }} />
                        {!set.logged ? (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '0.35rem 0.7rem', fontSize: '0.78rem' }}
                            onClick={() => logSet(exIdx, setIdx)}
                          >
                            Log Set
                          </button>
                        ) : (
                          <span style={{ color: theme.success, fontSize: '0.78rem', fontWeight: 600 }}>Done</span>
                        )}
                      </div>
                    ))}

                    <button className="btn btn-ghost" style={{ marginTop: '0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                      onClick={() => addSet(exIdx)}>+ Add Set</button>
                  </div>
                );
              })}
              <button className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '0.75rem 2rem' }} onClick={saveWorkout}>
                ⚡ Save Workout
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div>
          {logs.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
              No workout history yet. Start logging!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {logs.map(log => (
                <div key={log.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: theme.text, fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem' }}>{log.name}</div>
                      <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>{log.exercises?.length || 0} exercises</div>
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>
                      {log.loggedAt?.toDate ? format(log.loggedAt.toDate(), 'MMM d, yyyy') : 'Recent'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Exercise Detail Modal ── */}
      {selectedEx && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem', overflowY: 'auto',
        }} onClick={() => setSelectedEx(null)}>
          <div className="card" style={{ maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            {selectedEx.gifUrl && <GifDisplay url={selectedEx.gifUrl} theme={theme} />}
            <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', fontWeight: 700, textTransform: 'capitalize', marginBottom: '0.5rem', color: theme.text }}>
              {selectedEx.name}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              {[selectedEx.bodyPart, selectedEx.equipment, selectedEx.target].filter(Boolean).map((t, i) => (
                <span key={i} style={{ background: theme.border, color: theme.textSecondary, borderRadius: '20px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', textTransform: 'capitalize' }}>{t}</span>
              ))}
            </div>

            {/* Muscle Map */}
            <div style={{
              background: theme.bgSecondary,
              border: `1px solid ${theme.border}`,
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '1.25rem',
            }}>
              <div style={{ fontSize: '11px', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', fontFamily: "'Rajdhani', sans-serif" }}>
                Muscle Activation
              </div>
              <MuscleMap
                target={selectedEx.target}
                secondaryMuscles={selectedEx.secondaryMuscles || []}
              />
            </div>

            {/* Rest Recommendation */}
            {(() => {
              const rec = getRestRec(selectedEx);
              return (
                <div style={{
                  background: theme.bgSecondary,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '1.25rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '16px' }}>⏱</span>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: theme.accent, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Rest: {rec.label}
                    </span>
                  </div>
                  <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
                    {rec.explanation}
                  </p>
                </div>
              );
            })()}

            {selectedEx.instructions && (
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '11px', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontFamily: "'Rajdhani', sans-serif" }}>
                  Instructions
                </div>
                <ol style={{ paddingLeft: '1.25rem', color: theme.textSecondary, fontSize: '0.875rem', lineHeight: 1.7, margin: 0 }}>
                  {selectedEx.instructions.map((step, i) => <li key={i} style={{ marginBottom: '0.25rem' }}>{step}</li>)}
                </ol>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setSelectedEx(null)}>Close</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => { addToSession(selectedEx); setSelectedEx(null); }}>+ Add to Session</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rest Timer Banner ── */}
      {restState && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: theme.bgCard,
          borderTop: `2px solid ${theme.accent}`,
          padding: '12px 20px',
          zIndex: 900,
          display: 'flex', alignItems: 'center', gap: '16px',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
        }}>
          {/* Countdown */}
          <div style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: '34px',
            fontWeight: 700,
            color: restState.secondsLeft <= 10 ? theme.warning : theme.accent,
            minWidth: '64px',
            lineHeight: 1,
            transition: 'color 0.3s',
          }}>
            {Math.floor(restState.secondsLeft / 60)}:{String(restState.secondsLeft % 60).padStart(2, '0')}
          </div>

          {/* Progress + explanation */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
              <span style={{ fontWeight: 600 }}>Rest — {restState.label}</span>
              <span>{restState.secondsLeft}s left</span>
            </div>
            <div style={{ background: theme.border, borderRadius: '4px', height: '5px', marginBottom: '5px' }}>
              <div style={{
                background: restState.secondsLeft <= 10 ? theme.warning : theme.accent,
                height: '100%', borderRadius: '4px',
                width: `${(restState.secondsLeft / restState.maxSeconds) * 100}%`,
                transition: 'width 1s linear, background 0.3s',
              }} />
            </div>
            <p style={{ color: theme.textSecondary, fontSize: '11px', margin: 0, lineHeight: '1.4',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {restState.explanation}
            </p>
          </div>

          {/* Skip */}
          <button
            onClick={() => { clearTimer(); setRestState(null); }}
            style={{
              background: 'none', border: `1px solid ${theme.border}`,
              borderRadius: '8px', padding: '8px 14px',
              color: theme.textSecondary, cursor: 'pointer', fontSize: '13px',
              flexShrink: 0,
            }}
          >
            Skip
          </button>
        </div>
      )}
    </div>
  );
}
